import { UserProfileCreateDto, UserProfileRole } from '@@types/api.types.ts';
import { Modal } from '@components/core/modal.component.tsx';
import { ModalSteps, useSteps } from '@components/shared/modal-steps.component.tsx';
import { errorToast } from '@hooks/toast.hook.tsx';
import { useSubmitUserProfile, useUpdateUserProfile } from '@queries/user-profile.query.ts';
import { useGetCurrentUser } from '@queries/users.query.ts';
import {
  personalStepSchema,
  referrerStepSchema,
  roleStepSchema,
  skillsStepSchema,
  userProfileCreateSchema,
} from '@schemas/user-profile.schema.ts';
import { clearReferrerCodeSelector, referralStore, referrerCodeSelector } from '@stores/referral.store';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { ZodTypeAny, z } from 'zod';
import { useStore } from 'zustand';
import { PersonalStep } from './home-user-profile-modal-personal-step.component.tsx';
import { ReferrerStep } from './home-user-profile-modal-referrer-step.component.tsx';
import { RoleStep } from './home-user-profile-modal-role-step.component.tsx';
import { SkillsStep } from './home-user-profile-modal-skills-step.component.tsx';

type UserProfileModalProps = {
  isEdit?: boolean;
  isOpen: boolean;
  onClose: (submit?: boolean) => void;
  onContinue?: () => void;
};

type FormErrors = z.inferFormattedError<typeof userProfileCreateSchema, string>;

export type UserProfileComponentProps = {
  errors: FormErrors;
  formData: UserProfileCreateDto;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  isEdit?: boolean;
  setFormData: React.Dispatch<React.SetStateAction<UserProfileCreateDto>>;
};

type ExtendedUserProfileComponentProps = UserProfileComponentProps & {
  isRoleDisabled?: boolean;
  isReferrerDisabled?: boolean;
};

enum Steps {
  PERSONAL = 0,
  ROLE = 1,
  SKILLS = 2,
  REFERRER = 3,
}

type StepConfig = {
  component: FunctionComponent<UserProfileComponentProps | ExtendedUserProfileComponentProps>;
  guidance: string;
  label: string;
  schema: ZodTypeAny;
  step: Steps;
};

const stepData: StepConfig[] = [
  {
    component: PersonalStep,
    guidance: '',
    label: 'Personal',
    schema: personalStepSchema,
    step: Steps.PERSONAL,
  },
  {
    component: RoleStep,
    guidance: 'Which type of Tyr user are you?',
    label: 'Role',
    schema: roleStepSchema,
    step: Steps.ROLE,
  },
  {
    component: SkillsStep,
    guidance: 'Tell us about your professional background',
    label: 'Skills',
    schema: skillsStepSchema,
    step: Steps.SKILLS,
  },
  {
    component: ReferrerStep,
    guidance: 'Enter a Referral Code below to connect with your referrer',
    label: 'Referrer',
    schema: referrerStepSchema,
    step: Steps.REFERRER,
  },
];

const defaultFormData: UserProfileCreateDto = {
  categoryId: undefined,
  country: undefined,
  countryCode: '+1',
  firstName: '',
  hasAcceptedTerms: false,
  jobRoleId: undefined,
  lastName: '',
  phoneNumber: undefined,
  referrerCode: '',
  researchKeywordIds: [],
  role: UserProfileRole.INNOVATOR,
  subcategoryId: undefined,
  title: '',
};

const emptyErrors: FormErrors = { _errors: [] };

export const UserProfileModal: FunctionComponent<UserProfileModalProps> = (props) => {
  const { isOpen, onClose, isEdit = false, onContinue } = props;

  const [formData, setFormData] = useState<UserProfileCreateDto>(defaultFormData);
  const [initialFormData, setInitialFormData] = useState<UserProfileCreateDto>(defaultFormData);
  const [errors, setErrors] = useState<FormErrors>(emptyErrors);
  const { data: user } = useGetCurrentUser();

  const referrerCode = useStore(referralStore, referrerCodeSelector);
  const clearReferrerCode = useStore(referralStore, clearReferrerCodeSelector);

  const {
    step,
    stepsInfo,
    updateStepState,
    handleStepClick: handleStepClickBase,
    resetSteps,
  } = useSteps(stepData, Steps.PERSONAL, isEdit);

  useEffect(() => {
    let newFormData: UserProfileCreateDto;

    if (user?.profile) {
      const { jobRole, researchKeywords, ...profileData } = user.profile;
      newFormData = {
        ...defaultFormData,
        referrerCode: referrerCode || undefined,
        ...profileData,
        jobRoleId: jobRole?.id,
        researchKeywordIds: researchKeywords?.map((k) => k.id) ?? [],
      };
    } else {
      newFormData = {
        ...defaultFormData,
        referrerCode: referrerCode || undefined,
      };
    }

    setFormData(newFormData);
    setInitialFormData(newFormData);
  }, [user?.profile, referrerCode]);

  const { mutateAsync: submitUserProfile, isPending: isSubmittingProfile } = useSubmitUserProfile();
  const { mutateAsync: updateUserProfile, isPending: isUpdatingProfile } = useUpdateUserProfile();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const isWithinCommandOrPopover = (e.target as HTMLElement)?.closest('[role="combobox"]');

    if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement || isWithinCommandOrPopover)) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const validateCurrentStep = async () => {
    const currentStepConfig = stepData.find((s) => s.step === step);
    if (!currentStepConfig) return false;

    const result = currentStepConfig.schema.safeParse(formData);
    if (result.success) {
      setErrors(emptyErrors);
      return true;
    }

    setErrors(result.error.format());
    return false;
  };

  const onCloseOverride = () => {
    resetSteps();
    setFormData(initialFormData);
    setErrors(emptyErrors);
    onClose();
  };

  const handleSubmit = async () => {
    const result = userProfileCreateSchema.safeParse(formData);
    if (!result.success) {
      setErrors(result.error.format());
      errorToast({ description: 'Please fill in all required fields correctly.' });
      return;
    }

    try {
      if (isEdit) {
        const { role, ...formDataWithoutRole } = result.data;
        await updateUserProfile(formDataWithoutRole);
      } else {
        await submitUserProfile(result.data);
      }
      clearReferrerCode();

      if (step === Steps.REFERRER) {
        onContinue ? onContinue() : onCloseOverride();
      }
    } catch (error) {
      console.error('Error submitting form: ', error);
    }
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid) return;

    if (step < Steps.REFERRER) {
      if (isEdit) {
        await handleSubmit();
        onCloseOverride();
      } else {
        updateStepState(step);
      }
    } else {
      await handleSubmit();
    }
  };

  const handleStepClick = async (targetStep: Steps) => {
    if (targetStep === step) return;

    const isValid = await validateCurrentStep();
    if (!isValid) return;

    handleStepClickBase(targetStep);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const currentStepData = stepData.find((data) => data.step === step);

  return (
    <Modal
      footer={{
        rightButton: {
          loading: isSubmittingProfile || isUpdatingProfile,
          onClick: handleNext,
          children: isEdit ? 'Update' : 'Continue',
        },
      }}
      header={{
        title: 'User Profile',
      }}
      isOpen={isOpen}
      onClose={onCloseOverride}
      width="520px"
    >
      <div className="flex flex-col gap-4 py-2">
        <ModalSteps
          stepsInfo={stepsInfo}
          onStepClick={handleStepClick}
        />
      </div>

      {currentStepData?.guidance && (
        <div className="text-md font-normal mx-3 text-[#1F1C1AB2] flex whitespace-pre-wrap my-[20px] mb-[30px]">
          {currentStepData?.guidance}
        </div>
      )}

      <div className="px-3 pb-3">
        {currentStepData?.component && (
          <currentStepData.component
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            setFormData={setFormData}
            isEdit={isEdit}
            isRoleDisabled={isEdit && !!user?.profile?.role}
            isReferrerDisabled={isEdit && !!user?.profile?.referrerCode}
          />
        )}
      </div>
    </Modal>
  );
};
